import { Box, Divider, Grid, IconButton, InputAdornment, styled, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { getExportNewsLetter, getNewsLetter } from "../../api/staticPageApi";
import theme from "../../theme";
import { Add, BorderColor, Close, DeleteForever, Description, Search } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import moment from "moment";
import { createBlog, deleteBlog, getBlogList, updateBlog } from "../../api/blogApi";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import RichTextEditor from "react-rte";
import CommonEditor from "../../common/components/CommonEditor";
import { BlogStatus } from "../../common/const";
import Dropzone from "react-dropzone";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    padding: 10,
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
    height: 'auto',
    maxHeight: '400px',
    overflow: 'auto',
}));

const ManageBlogs = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const [addOpen, setAddOpen] = useState(false);
    const [blogs, setBlogs] = useState(null);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [editingBlog, setEditingBlog] = useState(null);
    const [value, setValue] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    
    // Validation error states
    const [titleError, setTitleError] = useState('');
    const [authorError, setAuthorError] = useState('');
    const [shortDescError, setShortDescError] = useState('');
    
    const [newBlog, setNewBlog] = useState({
        title: '',
        author_name: '',
        short_description: '',
        thumbnail: [],
        content: RichTextEditor?.createValueFromString(value || '', 'html'),
    });

    // Word count function
    const countWords = (text) => {
        return text.trim().split(/\s+/).filter(word => word.length > 0).length;
    };

    // BUG - 1: Validate Blog Title (must contain at least one letter)
    const validateTitle = (value) => {
        if (value.trim() === '') {
            setTitleError('');
            return true;
        }
        
        // Check if the value contains only numbers (no letters)
        const onlyNumbersRegex = /^[0-9\s]+$/;
        if (onlyNumbersRegex.test(value)) {
            setTitleError('Blog title cannot contain only numbers. Please add some text.');
            return false;
        }
        
        // Check if value has at least one letter
        const hasLetter = /[A-Za-z]/.test(value);
        if (!hasLetter) {
            setTitleError('Blog title must contain at least one letter');
            return false;
        }
        
        setTitleError('');
        return true;
    };

    // BUG - 2: Validate Author Name (must contain at least one letter)
    const validateAuthorName = (value) => {
        if (value.trim() === '') {
            setAuthorError('');
            return true;
        }
        
        // Check if the value contains only numbers (no letters)
        const onlyNumbersRegex = /^[0-9\s]+$/;
        if (onlyNumbersRegex.test(value)) {
            setAuthorError('Author name cannot contain only numbers. Please add some text.');
            return false;
        }
        
        // Check if value has at least one letter
        const hasLetter = /[A-Za-z]/.test(value);
        if (!hasLetter) {
            setAuthorError('Author name must contain at least one letter');
            return false;
        }
        
        setTitleError('');
        return true;
    };

    // BUG - 3: Validate Short Description (max 150 words)
    const validateShortDescription = (value) => {
        if (value.trim() === '') {
            setShortDescError('');
            return true;
        }
        
        const wordCount = countWords(value);
        if (wordCount > 150) {
            setShortDescError(`Short description cannot exceed 150 words. Current word count: ${wordCount}/150`);
            return false;
        }
        
        setShortDescError('');
        return true;
    };

    const getBlogs = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getBlogList(pageNo + 1, pageSize, searchTerm);
            setBlogs(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        getBlogs();
    }, [pageNo, pageSize, searchTerm]);

    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(0);
    };

    const handleDelete = async (id) => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await deleteBlog(id);
            if (response.success) {
                showNotification(response.message, 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setDeleteConfirm(null);
                getBlogs();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const handleOpenDialog = async (blog = null) => {
        setEditingBlog(blog);
        if (blog) {
            dispatch(setLoading({
                loading: true
            }))
            const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${blog.thumbnail_url}&download=false`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const blob = await responseData.blob();
            const imageUrl = URL.createObjectURL(blob);
            setNewBlog({
                title: blog.title,
                author_name: blog.author_name,
                short_description: blog.short_description,
                thumbnail: blog.thumbnail_url,
                thumbnail_url: imageUrl,
                content: RichTextEditor?.createValueFromString(blog.content || '', 'html')
            });
            dispatch(setLoading({
                loading: false
            }));
        } else {
            setNewBlog({
                title: '',
                author_name: '',
                short_description: '',
                thumbnail: [],
                content: RichTextEditor?.createValueFromString('', 'html')
            });
        }
        // Reset validation errors
        setTitleError('');
        setAuthorError('');
        setShortDescError('');
        setAddOpen(true);
    };

    const handleCloseDialog = () => {
        setEditingBlog(null);
        setValue('');
        setTitleError('');
        setAuthorError('');
        setShortDescError('');
        setNewBlog({
            title: '',
            author_name: '',
            short_description: '',
            thumbnail: [],
            content: RichTextEditor?.createValueFromString('', 'html')
        });
        setAddOpen(false);
    };

    const handleSaveBlog = async (status) => {
        // Validate all fields before saving
        const isTitleValid = validateTitle(newBlog.title);
        const isAuthorValid = validateAuthorName(newBlog.author_name);
        const isShortDescValid = validateShortDescription(newBlog.short_description);
        
        if (!newBlog.title.trim()) {
            showNotification('Please enter a blog title', 'error');
            return;
        }
        
        if (!isTitleValid) {
            showNotification(titleError, 'error');
            return;
        }
        
        if (!newBlog.author_name.trim()) {
            showNotification('Please enter an author name', 'error');
            return;
        }
        
        if (!isAuthorValid) {
            showNotification(authorError, 'error');
            return;
        }
        
        if (!newBlog.short_description.trim()) {
            showNotification('Please enter a short description', 'error');
            return;
        }
        
        if (!isShortDescValid) {
            showNotification(shortDescError, 'error');
            return;
        }
        
        try {
            if (newBlog.content.toString('markdown').length > 2) {
                dispatch(setLoading({
                    loading: true
                }));
                let response;
                const formData = new FormData();
                formData.append('title', newBlog.title);
                formData.append('author_name', newBlog.author_name);
                formData.append('short_description', newBlog.short_description);
                formData.append('content', newBlog.content?._cache?.html);
                formData.append('status', status);
                if (Array.isArray(newBlog?.thumbnail) && newBlog?.thumbnail?.length > 0) {
                    formData.append('thumbnail', newBlog.thumbnail[0])
                } else if (typeof newBlog?.thumbnail === 'string') {
                    formData.append('thumbnail_url', newBlog.thumbnail);
                }
                if (status === BlogStatus.Published) {
                    formData.append('published_at', new Date());
                }
                if (editingBlog) {
                    response = await updateBlog(editingBlog.blog_id, formData);
                } else {
                    response = await createBlog(formData);
                }
                if (response.success) {
                    showNotification(response.message, 'success');
                    dispatch(setLoading({
                        loading: false
                    }));
                    getBlogs();
                }
                handleCloseDialog();
            } else {
                showNotification('Please enter a content', 'error');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const handleDrop = acceptedFiles => {
        setNewBlog((prev) => ({
            ...prev,
            thumbnail: acceptedFiles?.map(files => files)
        }))
    };

    // BUG - 4: Search functionality
    const handleSearch = () => {
        setSearchTerm(searchInput);
        setPageNo(0);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setSearchTerm('');
        setPageNo(0);
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={!!deleteConfirm}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to permanently delete this blog post?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                handleDelete(deleteConfirm.blog_id)
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setDeleteConfirm(null)
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonDialog
                open={addOpen}
                size="sm"
                padding="0px"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{editingBlog ? 'Edit Blog' : 'Add New Blog'}</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    handleCloseDialog();
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.1} mb={1}>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Title *"
                                name="title"
                                value={newBlog.title}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setNewBlog({ ...newBlog, title: newValue });
                                    validateTitle(newValue);
                                }}
                                placeholder="Enter the title"
                                error={!!titleError}
                                helperText={titleError}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Author Name *"
                                name="author_name"
                                value={newBlog.author_name}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setNewBlog({ ...newBlog, author_name: newValue });
                                    validateAuthorName(newValue);
                                }}
                                placeholder="Enter the Author Name"
                                error={!!authorError}
                                helperText={authorError}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <CommonFormField
                                label="Short Description * (Max 150 words)"
                                name="short_description"
                                value={newBlog.short_description}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    const wordCount = countWords(newValue);
                                    if (wordCount <= 150) {
                                        setNewBlog({ ...newBlog, short_description: newValue });
                                        validateShortDescription(newValue);
                                    } else {
                                        showNotification(`Word limit exceeded! Maximum 150 words allowed. Current: ${wordCount}`, 'warning');
                                    }
                                }}
                                placeholder="Enter the Short Description"
                                multiline
                                rows={4}
                                error={!!shortDescError}
                                helperText={shortDescError || `Word count: ${countWords(newBlog.short_description)}/150`}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <Box sx={{ width: '100%' }}>
                                {typeof newBlog?.thumbnail === 'string' ? (
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            display: 'inline-block'
                                        }}
                                    >
                                        <img
                                            src={newBlog.thumbnail_url}
                                            height="150px"
                                            width="150px"
                                            style={{ display: 'block' }}
                                            alt="thumbnail"
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => setNewBlog((prev) => ({
                                                ...prev,
                                                thumbnail: []
                                            }))}
                                            color="error"
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 1)'
                                                },
                                                padding: '4px',
                                                minWidth: 'auto',
                                                minHeight: 'auto'
                                            }}
                                        >
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ) : newBlog?.thumbnail?.length > 0 ? (
                                    <Box sx={{ mt: 2 }}>
                                        {newBlog?.thumbnail?.map((file, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 2,
                                                    border: '1px solid',
                                                    borderColor: 'grey.300',
                                                    borderRadius: 1,
                                                    mb: 1,
                                                    backgroundColor: 'background.paper'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Description color="primary" />
                                                    <Box>
                                                        <Typography variant="body1">{file.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {(file.size / 1024).toFixed(2)} KB
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        const updatedThumbnails = [...newBlog.thumbnail];
                                                        updatedThumbnails.splice(index, 1);
                                                        setNewBlog((prev) => ({
                                                            ...prev,
                                                            thumbnail: updatedThumbnails
                                                        }));
                                                    }}
                                                    color="error"
                                                >
                                                    <Close />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Dropzone
                                        onDrop={handleDrop}
                                        accept={{
                                            'image/jpeg': ['.jpg', '.jpeg'],
                                            'image/png': ['.png']
                                        }}
                                        maxFiles={1}
                                    >
                                        {({ getRootProps, getInputProps, isDragActive }) => (
                                            <Box
                                                {...getRootProps()}
                                                sx={{
                                                    border: '2px dashed',
                                                    borderColor: '#D8DAEB',
                                                    borderRadius: 2,
                                                    padding: { xs: 3, md: 6 },
                                                    textAlign: 'center',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    minHeight: 100,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <input {...getInputProps()} />
                                                <img src="/Images/sound.png" alt="upload" />
                                                <Typography fontWeight={600} gutterBottom>
                                                    Drag and drop your files
                                                </Typography>
                                                <CommonButton
                                                    label="Browse"
                                                    color="Black"
                                                    background="White"
                                                    border={`1px solid ${theme.palette.background.secondary}`}
                                                    width="100px"
                                                    height="40px"
                                                    marginBottom="0px"
                                                    marginTop="10px"
                                                />
                                            </Box>
                                        )}
                                    </Dropzone>
                                )}
                            </Box>
                        </Grid>
                        <Grid item md={12}>
                            <CommonEditor
                                placeholder="Enter the content"
                                value={newBlog.content}
                                setValue={(value) => {
                                    setValue(value.toString('html'));
                                    setNewBlog({ ...newBlog, content: value })
                                }}
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Save Changes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.background.main}
                            fontWeight={700}
                            marginBottom="10px"
                            disabled={!newBlog.title.trim() || !newBlog.author_name.trim() || !newBlog.short_description.trim() || !!titleError || !!authorError || !!shortDescError}
                            handleClick={() => {
                                handleSaveBlog(BlogStatus.Published);
                            }}
                        />
                        <CommonButton
                            label="Save As Draft"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.background.main}
                            fontWeight={700}
                            marginBottom="10px"
                            disabled={!newBlog.title.trim() || !newBlog.author_name.trim() || !newBlog.short_description.trim() || !!titleError || !!authorError || !!shortDescError}
                            handleClick={() => {
                                handleSaveBlog(BlogStatus.Draft);
                            }}
                        />
                    </Fragment>
                )}
            />
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Typography fontSize={20} fontWeight={600}>Manage Blogs</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* BUG - 4: Search Input */}
                    <TextField
                        size="small"
                        placeholder="Search by title"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: 'gray' }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchInput && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={handleClearSearch}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ width: '250px' }}
                    />
                    <CommonButton
                        label="Search"
                        marginBottom="0px"
                        width="100px"
                        fontWeight={600}
                        background={theme.palette.secondary.main}
                        color="White"
                        handleClick={handleSearch}
                    />
                    <CommonButton
                        label="Add Blog"
                        marginBottom="0px"
                        width="140px"
                        fontWeight={600}
                        startIcon={<Add />}
                        background={theme.palette.secondary.main}
                        color="White"
                        handleClick={() => setAddOpen(true)}
                    />
                </Box>
            </Box>
            <Box pt={1} pb={0}>
                <StyledTableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <Cell width={'25%'}>Blog Title</Cell>
                                <Cell width={'20%'}>Author</Cell>
                                <Cell width={'20%'}>Status</Cell>
                                <Cell width={'15%'}>Published Date</Cell>
                                <Cell>Actions</Cell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {blogs?.data.length > 0 ? blogs?.data?.map((blog) => (
                                <TableRow key={blog.blog_id}>
                                    <InnerCell style={{ color: 'black' }}>
                                        {blog.title}
                                    </InnerCell>
                                    <InnerCell style={{ color: 'black' }}>
                                        {blog.author_name}
                                    </InnerCell>
                                    <InnerCell>
                                        {blog.status}
                                    </InnerCell>
                                    <InnerCell>
                                        {blog.status === BlogStatus.Published && blog.published_at ? moment(blog.published_at).format('MMM DD, YYYY') : '-'}
                                    </InnerCell>
                                    <InnerCell>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleOpenDialog(blog)}
                                        >
                                            <BorderColor sx={{ color: theme.palette.secondary.main, fontSize: 17 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => setDeleteConfirm(blog)}
                                        >
                                            <DeleteForever sx={{ color: 'red', fontSize: 20 }} />
                                        </IconButton>
                                    </InnerCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography>No Blogs Found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            </Box>

            {/* Pagination Component */}
            {blogs?.pagination?.total > 0 && (
                <Box sx={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                        component="div"
                        count={blogs?.pagination?.total}
                        rowsPerPage={pageSize}
                        page={pageNo}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: '0.875rem',
                            },
                        }}
                    />
                </Box>
            )}
        </Fragment>
    )
}

export default ManageBlogs;